import { ArrowLeft, LoaderCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import FormularioIngreso from '../components/ingresos/FormularioIngreso'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'

const CAMPOS_INGRESO = `
  id,
  empresa_id,
  cliente_id,
  fecha_ingreso,
  fecha_vencimiento,
  fecha_pago,
  tipo_documento,
  numero_documento,
  descripcion,
  monto_neto,
  monto_iva,
  monto_exento,
  otros_impuestos,
  monto_total,
  medio_pago,
  estado_pago,
  estado_registro,
  observaciones,
  creado_por,
  creado_en,
  actualizado_en
`

function EditarIngreso() {
  const { ingresoId } = useParams()
  const navigate = useNavigate()
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, cerrarModal } = useModalMensaje()

  const [ingreso, setIngreso] = useState(null)
  const [cargandoIngreso, setCargandoIngreso] = useState(true)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    cargarIngreso()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingresoId, empresaActual?.id])

  const cargarIngreso = async () => {
    if (!ingresoId || !empresaActual?.id) return

    try {
      setCargandoIngreso(true)

      const { data, error: errorConsulta } = await supabase
        .from('ingresos')
        .select(CAMPOS_INGRESO)
        .eq('id', ingresoId)
        .eq('empresa_id', empresaActual.id)
        .neq('estado_registro', 'anulado')
        .maybeSingle()

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      if (!data) {
        setIngreso(null)

        abrirModal({
          tipo: 'error',
          titulo: 'Ingreso no encontrado',
          mensaje:
            'El ingreso no existe, fue eliminado o no pertenece a esta empresa.',
          onAceptar: () => {
            cerrarModal()
            navigate('/ingresos')
          },
        })
        return
      }

      setIngreso(data)
    } catch (errorCarga) {
      console.error('Error al cargar ingreso:', errorCarga)

      if (!montadoRef.current) return

      setIngreso(null)

      abrirModal({
        tipo: 'error',
        titulo: 'Error al cargar el ingreso',
        mensaje:
          errorCarga.message ||
          'No fue posible cargar el ingreso.',
        textoAceptar: 'Reintentar',
        textoCancelar: 'Volver',
        mostrarCancelar: true,
        onAceptar: () => {
          cerrarModal()
          cargarIngreso()
        },
        onCancelar: () => {
          cerrarModal()
          navigate('/ingresos')
        },
      })
    } finally {
      if (montadoRef.current) {
        setCargandoIngreso(false)
      }
    }
  }

  const guardarEdicion = async (datosFormulario) => {
    const { error: errorActualizar } = await supabase
      .from('ingresos')
      .update(datosFormulario)
      .eq('id', ingresoId)
      .eq('empresa_id', empresaActual.id)
      .neq('estado_registro', 'anulado')
      .select(CAMPOS_INGRESO)
      .single()

    if (errorActualizar) {
      if (errorActualizar.code === 'PGRST116') {
        const { data: ingresoActual } = await supabase
          .from('ingresos')
          .select('estado_registro')
          .eq('id', ingresoId)
          .eq('empresa_id', empresaActual.id)
          .maybeSingle()

        if (ingresoActual?.estado_registro === 'anulado') {
          throw new Error(
            'Este ingreso fue anulado mientras lo estabas editando.'
          )
        }

        throw new Error(
          'No tienes permisos para modificar este ingreso.'
        )
      }

      throw errorActualizar
    }

    abrirModal({
      tipo: 'success',
      titulo: 'Ingreso actualizado',
      mensaje: 'Los cambios fueron guardados correctamente.',
      onAceptar: () => {
        cerrarModal()
        navigate('/ingresos', { state: { recargar: true } })
      },
    })
  }

  if (cargandoIngreso) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={38}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando ingreso...
          </p>
        </div>
      </div>
    )
  }

  if (!ingreso) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() => navigate('/ingresos')}
          className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
        >
          <ArrowLeft size={18} />
          Volver a ingresos
        </button>

        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <p className="text-slate-500">
            No fue posible mostrar este ingreso.
          </p>
        </div>

        <ModalMensaje {...modal} />
      </div>
    )
  }

  return (
    <>
      <FormularioIngreso
        modo="editar"
        ingresoInicial={ingreso}
        onGuardar={guardarEdicion}
        onCancelar={() => navigate('/ingresos')}
      />

      <ModalMensaje {...modal} />
    </>
  )
}

export default EditarIngreso
